import * as z from 'zod';

export const GetLoyaltyTransactionRequest = z.object({
  loyaltyTransactionId: z
    .string()
    .describe('ID of the loyalty transaction to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetLoyaltyTransactionResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Transaction ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    accountId: z
      .string()
      .describe('Account ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    _createdDate: z
      .date()
      .describe('Date and time the transaction was created.')
      .optional()
      .nullable(),
    amount: z
      .number()
      .int()
      .describe(
        "The number of points earned, adjusted, redeemed, or expired in the transaction.\n\n+ `EARN`: Number of points earned by a customer taking an action.\n+ `REDEEM`: The number of points redeemed by a customer for a reward.\n+ `ADJUST`: The number of points adjusted to a customer's balance.\n+ `REFUND`: The number of points refunded for a previously redeemed reward.\n+ `EXPIRE`: The number of points that expired due to customer inactivity for a configured period."
      )
      .optional(),
    transactionType: z
      .enum([
        'UNKNOWN',
        'EARN',
        'REDEEM',
        'ADJUST',
        'REFUND',
        'EXPIRE',
        'EARN_ATTEMPT',
      ])
      .describe('Type of transaction.')
      .optional(),
    description: z
      .string()
      .describe('Transaction description.')
      .max(100)
      .optional(),
    idempotencyKey: z
      .string()
      .describe(
        'Unique identifier, generated by the client. Used to recognize repeated attempts of the same request. Only present when manually adding points with [Earn Points](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/earn-points).'
      )
      .max(128)
      .optional(),
  }),
  z.xor([
    z.object({
      earnInfo: z.never().optional(),
      redeemInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
    }),
    z.object({
      redeemInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
      earnInfo: z
        .intersection(
          z.object({
            appId: z
              .string()
              .describe('ID of the app that generated the earned points.')
              .min(1)
              .optional(),
            activityType: z.string().describe('Activity type.').optional(),
            orderId: z
              .string()
              .describe('Order ID.')
              .max(512)
              .optional()
              .nullable(),
            limitApplied: z
              .boolean()
              .describe(
                'Whether the points amount was adjusted by an earn points limit provider.'
              )
              .optional(),
          }),
          z.xor([
            z.object({ followedSocialMedia: z.never().optional() }),
            z.object({
              followedSocialMedia: z
                .object({
                  channel: z
                    .string()
                    .describe(
                      'Social media channel.\nFor example, `"FACEBOOK"` or `"INSTAGRAM"`.'
                    )
                    .max(32)
                    .optional(),
                })
                .describe('Details on following on social media.'),
            }),
          ])
        )
        .describe('Information on points earned.'),
    }),
    z.object({
      earnInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
      redeemInfo: z
        .object({
          rewardId: z
            .string()
            .describe('ID of the reward that was redeemed.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          rewardType: z
            .string()
            .describe('Type of reward that was redeemed.')
            .optional()
            .nullable(),
          referenceEntityId: z
            .string()
            .describe(
              "ID of the specific item that was redeemed in this transaction.\n\nEach reward that's redeemed has a unique `referenceEntityId`."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe('Information on points redeemed.'),
    }),
    z.object({
      earnInfo: z.never().optional(),
      redeemInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
      adjustInfo: z
        .object({
          appId: z
            .string()
            .describe('ID of the app that initiated the points adjustment.')
            .min(1)
            .optional(),
          amountBefore: z
            .number()
            .int()
            .describe('Amount before adjustment.')
            .optional(),
          amountAfter: z
            .number()
            .int()
            .describe('Amount after adjustment.')
            .optional(),
        })
        .describe('Information on points adjusted.'),
    }),
    z.object({
      earnInfo: z.never().optional(),
      redeemInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
      refundInfo: z
        .object({
          appId: z
            .string()
            .describe('ID of the app that refunded points.')
            .min(1)
            .optional(),
          refundedTransactionId: z
            .string()
            .describe('ID of the transaction that was refunded.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe('Information on points refunded.'),
    }),
    z.object({
      earnInfo: z.never().optional(),
      redeemInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      earnAttemptInfo: z.never().optional(),
      expireInfo: z
        .object({
          amountBefore: z
            .number()
            .int()
            .describe('Amount before expiration.')
            .optional(),
          amountAfter: z
            .number()
            .int()
            .describe('Amount after expiration.')
            .optional(),
        })
        .describe('Information on points expired.'),
    }),
    z.object({
      earnInfo: z.never().optional(),
      redeemInfo: z.never().optional(),
      adjustInfo: z.never().optional(),
      refundInfo: z.never().optional(),
      expireInfo: z.never().optional(),
      earnAttemptInfo: z
        .object({
          appId: z
            .string()
            .describe('ID of the app that generated the earn operation.')
            .min(1)
            .optional(),
          activityType: z.string().describe('Activity type.').optional(),
          orderId: z
            .string()
            .describe('Order ID.')
            .max(512)
            .optional()
            .nullable(),
        })
        .describe(
          'Information on a limited earn attempt recorded with 0 points.'
        ),
    }),
  ])
);
export const QueryLoyaltyTransactionsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          accountId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          idempotencyKey: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          transactionType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          amount: z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'accountId',
                'idempotencyKey',
                'transactionType',
                'amount',
                'description',
                '_createdDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryLoyaltyTransactionsResponse = z.object({
  loyaltyTransactions: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Transaction ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          accountId: z
            .string()
            .describe('Account ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          _createdDate: z
            .date()
            .describe('Date and time the transaction was created.')
            .optional()
            .nullable(),
          amount: z
            .number()
            .int()
            .describe(
              "The number of points earned, adjusted, redeemed, or expired in the transaction.\n\n+ `EARN`: Number of points earned by a customer taking an action.\n+ `REDEEM`: The number of points redeemed by a customer for a reward.\n+ `ADJUST`: The number of points adjusted to a customer's balance.\n+ `REFUND`: The number of points refunded for a previously redeemed reward.\n+ `EXPIRE`: The number of points that expired due to customer inactivity for a configured period."
            )
            .optional(),
          transactionType: z
            .enum([
              'UNKNOWN',
              'EARN',
              'REDEEM',
              'ADJUST',
              'REFUND',
              'EXPIRE',
              'EARN_ATTEMPT',
            ])
            .describe('Type of transaction.')
            .optional(),
          description: z
            .string()
            .describe('Transaction description.')
            .max(100)
            .optional(),
          idempotencyKey: z
            .string()
            .describe(
              'Unique identifier, generated by the client. Used to recognize repeated attempts of the same request. Only present when manually adding points with [Earn Points](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/earn-points).'
            )
            .max(128)
            .optional(),
        }),
        z.xor([
          z.object({
            earnInfo: z.never().optional(),
            redeemInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
          }),
          z.object({
            redeemInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
            earnInfo: z
              .intersection(
                z.object({
                  appId: z
                    .string()
                    .describe('ID of the app that generated the earned points.')
                    .min(1)
                    .optional(),
                  activityType: z
                    .string()
                    .describe('Activity type.')
                    .optional(),
                  orderId: z
                    .string()
                    .describe('Order ID.')
                    .max(512)
                    .optional()
                    .nullable(),
                  limitApplied: z
                    .boolean()
                    .describe(
                      'Whether the points amount was adjusted by an earn points limit provider.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({ followedSocialMedia: z.never().optional() }),
                  z.object({
                    followedSocialMedia: z
                      .object({
                        channel: z
                          .string()
                          .describe(
                            'Social media channel.\nFor example, `"FACEBOOK"` or `"INSTAGRAM"`.'
                          )
                          .max(32)
                          .optional(),
                      })
                      .describe('Details on following on social media.'),
                  }),
                ])
              )
              .describe('Information on points earned.'),
          }),
          z.object({
            earnInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
            redeemInfo: z
              .object({
                rewardId: z
                  .string()
                  .describe('ID of the reward that was redeemed.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                rewardType: z
                  .string()
                  .describe('Type of reward that was redeemed.')
                  .optional()
                  .nullable(),
                referenceEntityId: z
                  .string()
                  .describe(
                    "ID of the specific item that was redeemed in this transaction.\n\nEach reward that's redeemed has a unique `referenceEntityId`."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information on points redeemed.'),
          }),
          z.object({
            earnInfo: z.never().optional(),
            redeemInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
            adjustInfo: z
              .object({
                appId: z
                  .string()
                  .describe(
                    'ID of the app that initiated the points adjustment.'
                  )
                  .min(1)
                  .optional(),
                amountBefore: z
                  .number()
                  .int()
                  .describe('Amount before adjustment.')
                  .optional(),
                amountAfter: z
                  .number()
                  .int()
                  .describe('Amount after adjustment.')
                  .optional(),
              })
              .describe('Information on points adjusted.'),
          }),
          z.object({
            earnInfo: z.never().optional(),
            redeemInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
            refundInfo: z
              .object({
                appId: z
                  .string()
                  .describe('ID of the app that refunded points.')
                  .min(1)
                  .optional(),
                refundedTransactionId: z
                  .string()
                  .describe('ID of the transaction that was refunded.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Information on points refunded.'),
          }),
          z.object({
            earnInfo: z.never().optional(),
            redeemInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            earnAttemptInfo: z.never().optional(),
            expireInfo: z
              .object({
                amountBefore: z
                  .number()
                  .int()
                  .describe('Amount before expiration.')
                  .optional(),
                amountAfter: z
                  .number()
                  .int()
                  .describe('Amount after expiration.')
                  .optional(),
              })
              .describe('Information on points expired.'),
          }),
          z.object({
            earnInfo: z.never().optional(),
            redeemInfo: z.never().optional(),
            adjustInfo: z.never().optional(),
            refundInfo: z.never().optional(),
            expireInfo: z.never().optional(),
            earnAttemptInfo: z
              .object({
                appId: z
                  .string()
                  .describe('ID of the app that generated the earn operation.')
                  .min(1)
                  .optional(),
                activityType: z.string().describe('Activity type.').optional(),
                orderId: z
                  .string()
                  .describe('Order ID.')
                  .max(512)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Information on a limited earn attempt recorded with 0 points.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
